/**
 * The reference and state screens: 5a · 4d · 10a · 10i · 11a · 11b · 9g · 9h · 9i · 12d.
 *
 * These document rules and show examples rather than a school's own data, so the design
 * markup *is* the screen — there is nothing to bind. They are routed straight to their
 * transcriptions, wrapped only so the design's own links navigate.
 *
 * That the compatibility table (4d) and the mastery model (5a) match `/api/reference/*`
 * is not a coincidence to maintain by hand: the API generates its payload from the same
 * `domain/rules.ts` and `domain/mastery.ts` the runtime enforces, so if the two ever
 * disagree it is the transcription that is stale, and `scripts/transcribe.py` regenerates it.
 */

import { Interactive, type RouteMap } from '../design/interactive';

import { Screen5A } from '../design/Screen5A';
import { Screen4D } from '../design/Screen4D';
import { Screen10A } from '../design/Screen10A';
import { Screen10I } from '../design/Screen10I';
import { Screen11A } from '../design/Screen11A';
import { Screen11B } from '../design/Screen11B';
import { Screen9G } from '../design/Screen9G';
import { Screen9H } from '../design/Screen9H';
import { Screen9I } from '../design/Screen9I';
import { Screen12D } from '../design/Screen12D';

/** The design's own cross-links between reference screens. */
const ROUTES: RouteMap = {
  dashboard: '/teacher',
  work: '/teacher/work',
  plans: '/teacher/plans',
  students: '/teacher/students',
  'learning plans': '/teacher/plans',
  'learning events': '/teacher/work',
  'e-assessments': '/teacher/assessments',
  home: '/student',
  overview: '/admin',
  teachers: '/admin/teachers',
};

function Ref({ children }: { children: React.ReactNode }) {
  /*
   * These are transcriptions of the design at the width it was drawn, and they are
   * regenerated by `scripts/transcribe.py` — reflowing them for a phone would make them
   * something other than what they document. So the wide artefact scrolls inside its own
   * box rather than taking the page body with it, which is the rule the rest of the app
   * follows for tables and diagrams. Without this, four of them scrolled the whole page
   * sideways on a 420px screen.
   */
  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Interactive routes={ROUTES}>{children}</Interactive>
    </div>
  );
}

export const MasteryModel = () => <Ref><Screen5A /></Ref>;
export const Compatibility = () => <Ref><Screen4D /></Ref>;
export const VocabularyMap = () => <Ref><Screen10A /></Ref>;
export const AccessibilityRules = () => <Ref><Screen10I /></Ref>;
export const CopyRules = () => <Ref><Screen11A /></Ref>;
export const FlowReview = () => <Ref><Screen11B /></Ref>;
export const LiveEventDialogs = () => <Ref><Screen9G /></Ref>;
export const PlanResultDialogs = () => <Ref><Screen9H /></Ref>;
export const StatesShowcase = () => <Ref><Screen9I /></Ref>;
export const EmptyStates = () => <Ref><Screen12D /></Ref>;
